import { maxBy, minBy, take, takeRight } from 'es-toolkit';

import { CalculationError, OrderedCuts, TopOrBottomCut, TrussEnvelope } from '@paragon/contract-library';
import {
  Direction2D,
  Direction3D,
  distanceEquals,
  IsometricEmbedding,
  Line2D,
  Plane3D,
  Point2D,
  Point3D,
  Polygon2D,
  Polygon3D,
} from '@paragon/geometry-library';
import {
  assertExhaustiveSwitch,
  circularPairwise,
  notNullish,
  pairwise,
  Result,
  tripletWise,
  wrapErr,
  wrapOk,
} from '@paragon/utilities';

import { CalculationErrorFactory } from './calculationErrors';
import { minimalHull } from './roof-solids';
import { RoofPlaneSide } from './truss-solids';

type RelevantTrussEnvelopeProperties = Pick<
  TrussEnvelope,
  | 'topCuts'
  | 'bottomCuts'
  | 'orderedTopCuts'
  | 'orderedBottomCuts'
  | 'containerLevel'
  | 'capLevelOne'
  | 'capLevelTwo'
  | 'featurePatternContainerGuid'
>;

/** Represents the cuts (both infinite and stepwise) that apply to a truss envelope's shape. */
export class TrussEnvelopeCuts {
  /** The infinite cuts to apply to either side of the truss solid, oriented inward. */
  public readonly planeCuts: readonly Plane3D[];
  /** If not empty, the stepwise cuts to apply left-to-right to form the top side of the truss solid. */
  public readonly orderedTopCuts: readonly Plane3D[];
  /** If not empty, the stepwise cuts to apply left-to-right to form the bottom side of the truss solid. */
  public readonly orderedBottomCuts: readonly Plane3D[];

  private constructor(
    planeCuts: readonly Plane3D[],
    orderedTopCuts: readonly Plane3D[],
    orderedBottomCuts: readonly Plane3D[],
  ) {
    this.planeCuts = planeCuts;
    this.orderedTopCuts = orderedTopCuts;
    this.orderedBottomCuts = orderedBottomCuts;
  }

  public static fromTrussEnvelope(
    trussEnvelope: RelevantTrussEnvelopeProperties,
    calculateRoofPlane: (roofPlaneGuid: string, side: RoofPlaneSide) => Plane3D | null,
    calculateCeilingPlane: (ceilingPlaneGuid: string) => Plane3D | null,
  ): TrussEnvelopeCuts | null {
    const { top: topCapPlane, bottom: bottomCapPlane } = TrussEnvelopeCuts.getCappingPlanes(trussEnvelope);

    const computeCut =
      (roofPlaneSide: RoofPlaneSide) =>
      (topOrBottomCut: TopOrBottomCut): Plane3D | null => {
        switch (topOrBottomCut.type) {
          case 'Absolute':
            return Plane3D.fromObject(topOrBottomCut.plane);
          case 'RoofPlane':
            return calculateRoofPlane(topOrBottomCut.roofPlaneGuid, roofPlaneSide);
          case 'CeilingPlane':
            return calculateCeilingPlane(topOrBottomCut.ceilingPlaneGuid);
          default:
            assertExhaustiveSwitch(topOrBottomCut);
        }
      };
    // The sides appear swapped because the top of an envelope is cut against the bottom of planes, and vice versa.
    const computeTopCut = computeCut('Bottom');
    const computeBottomCut = computeCut('Top');

    let topCutSlicingPlanes: Plane3D[] = [];
    if (trussEnvelope.orderedTopCuts.cuts.length === 0) {
      const topCutPlanes = trussEnvelope.topCuts
        .map(computeTopCut)
        .map((plane) => plane?.orientNormalWith(Direction3D.negativeZ));
      if (!topCutPlanes.every(notNullish)) return null;
      topCutSlicingPlanes = [...topCutPlanes, topCapPlane].filter(notNullish);
    }

    let bottomCutSlicingPlanes: Plane3D[] = [];
    if (trussEnvelope.orderedBottomCuts.cuts.length === 0) {
      const bottomCutPlanes = trussEnvelope.bottomCuts
        .map(computeBottomCut)
        .map((plane) => plane?.orientNormalWith(Direction3D.positiveZ));
      if (!bottomCutPlanes.every(notNullish)) return null;
      bottomCutSlicingPlanes = [...bottomCutPlanes, bottomCapPlane].filter(notNullish);
    }

    const produceOrderedCuts = (
      { cuts, reversed }: OrderedCuts,
      producer: (topOrBottomCut: TopOrBottomCut) => Plane3D | null,
    ) => {
      const cutPlanes = cuts.map(producer);
      return reversed ? cutPlanes.toReversed() : cutPlanes;
    };
    const orderedTopCuts = produceOrderedCuts(trussEnvelope.orderedTopCuts, computeTopCut);
    const orderedBottomCuts = produceOrderedCuts(trussEnvelope.orderedBottomCuts, computeBottomCut);
    if (!orderedTopCuts.every(notNullish) || !orderedBottomCuts.every(notNullish)) return null;

    return new TrussEnvelopeCuts(
      [...topCutSlicingPlanes, ...bottomCutSlicingPlanes],
      orderedTopCuts,
      orderedBottomCuts,
    );
  }

  private static getCappingPlanes(trussEnvelope: RelevantTrussEnvelopeProperties): {
    top: Plane3D | null;
    bottom: Plane3D | null;
  } {
    // Feature pattern truss envelopes do not respect any cap levels
    if (trussEnvelope.featurePatternContainerGuid != null) return { top: null, bottom: null };

    const capLevelOnePoint = new Point3D(0, 0, trussEnvelope.capLevelOne);
    const capLevelTwoPoint = new Point3D(0, 0, trussEnvelope.capLevelTwo);

    switch (trussEnvelope.containerLevel) {
      case 'BaseLevel':
        return {
          top: Plane3D.fromPerpendicularDirectionAndPoint(Direction3D.negativeZ, capLevelOnePoint),
          bottom: null,
        };
      case 'CapLevelOne':
        return {
          top: Plane3D.fromPerpendicularDirectionAndPoint(Direction3D.negativeZ, capLevelTwoPoint),
          bottom: Plane3D.fromPerpendicularDirectionAndPoint(Direction3D.positiveZ, capLevelOnePoint),
        };
      case 'CapLevelTwo':
        return {
          top: null,
          bottom: Plane3D.fromPerpendicularDirectionAndPoint(Direction3D.positiveZ, capLevelTwoPoint),
        };
      case 'NotCapped':
        return { top: null, bottom: null };
      default:
        assertExhaustiveSwitch(trussEnvelope.containerLevel);
    }
  }

  /**
   * Calculates the vertices associated with walking the provided cuts counterclockwise between the provided boundary
   * lines and planes.
   * @param orderedCuts The non-empty list of cuts producing the boundary
   */
  public static calculateBoundaryPoints(
    leftLine: Line2D,
    rightLine: Line2D,
    backPlane: Plane3D,
    frontPlane: Plane3D,
    backPlaneEmbedding: IsometricEmbedding,
    orderedCuts: readonly Plane3D[],
    top: boolean,
    { allowPartial } = { allowPartial: false },
  ): Result<{ cutFacets: readonly Polygon3D[]; boundary: readonly Point2D[] | null }, CalculationError> {
    // Preliminary checks
    if (orderedCuts.length === 0) throw new Error('Expected non-empty cuts to produce truss boundary');
    // If the first or last plane is parallel to the left/right lines, we can't form the leftmost/rightmost point
    if (
      !allowPartial &&
      (orderedCuts[0].halfSpace.direction.isPerpendicularTo(Direction3D.negativeZ) ||
        orderedCuts[orderedCuts.length - 1].halfSpace.direction.isPerpendicularTo(Direction3D.negativeZ))
    )
      return wrapErr(CalculationErrorFactory.verticalTerminalOrderedCuts(top));
    // If any plane is parallel to the truss face planes, it can't form a cut against the truss shape
    if (orderedCuts.some((cutPlane) => cutPlane.isParallelTo(backPlane)))
      return wrapErr(CalculationErrorFactory.orderedCutsParallelToFace(top));
    // If consecutive planes are parallel, we can't find their line of intersection
    if (pairwise(orderedCuts).some(([cutPlane, nextCutPlane]) => cutPlane.isParallelTo(nextCutPlane)))
      return wrapErr(CalculationErrorFactory.consecutiveParallelOrderedCuts(top));

    const leftPlane = Plane3D.fromPerpendicularDirectionAndPoint(
      backPlaneEmbedding.directionTo3D(Direction2D.right),
      backPlaneEmbedding.pointTo3D(leftLine.basePoint),
    );
    const rightPlane = Plane3D.fromPerpendicularDirectionAndPoint(
      backPlaneEmbedding.directionTo3D(Direction2D.right),
      backPlaneEmbedding.pointTo3D(rightLine.basePoint),
    );

    const cutFacets = tripletWise([leftPlane, ...orderedCuts, rightPlane]).map(([previous, current, next]) => {
      // Find the quadrilateral formed within the current plane by intersecting with its four neighbors
      const vertices = circularPairwise(
        [previous, frontPlane, next, backPlane].map((plane) => current.intersectWithPlane(plane)),
      )
        .map(([line1, line2]) => line1 && line2 && line1.intersectWithLine(line2))
        .filter((vertex) => vertex instanceof Point3D);
      try {
        if (vertices.length === 4) return { partial: false, shape: new Polygon3D(vertices) };
        if (!allowPartial) return null;
        // allowPartial === true, so we need to see if we can compute a hint polygon from a single intersecting plane
        if (vertices.length !== 2) return null;
        const [vertex1, vertex2] = vertices;
        // if this plane isn't vertical, we can't produce a hint for it (it would have succeeded above already)
        if (!current.halfSpace.direction.isPerpendicularTo(Direction3D.negativeZ)) return null;
        return {
          partial: true,
          shape: new Polygon3D([
            vertex1.addZ(VERTICAL_PLANE_HINT_EXPANSION),
            vertex1.addZ(-VERTICAL_PLANE_HINT_EXPANSION),
            vertex2.addZ(-VERTICAL_PLANE_HINT_EXPANSION),
            vertex2.addZ(VERTICAL_PLANE_HINT_EXPANSION),
          ]),
        };
      } catch {
        return null;
      }
    });
    const shapes = cutFacets.filter(notNullish).map(({ shape }) => shape);
    const isPartial = cutFacets.some((cutFacet) => cutFacet == null || cutFacet.partial);
    if (isPartial) {
      return allowPartial
        ? wrapOk({ cutFacets: shapes, boundary: null })
        : wrapErr(CalculationErrorFactory.nullCutFacet(top));
    }
    const uncleanedHull = minimalHull(
      shapes
        .flatMap(({ edges }) => edges)
        .map((edge) => edge.projectOntoPlane(backPlane))
        .filter(notNullish)
        .map((edge) => backPlaneEmbedding.segmentTo2D(edge)),
    );
    if (uncleanedHull == null) return wrapErr(CalculationErrorFactory.boundaryFailure(top, true));
    // The 2D projection of the cut facets into backPlane, represented as a boundary formed by points
    const cutFacetsHull = Polygon2D.removeIntermediateCollinearPoints(uncleanedHull);

    // Find starting/ending hull points along left and right lines
    const extremaBy = top ? minBy : maxBy;
    const leftPoint = extremaBy(
      cutFacetsHull.filter((point) => distanceEquals(point.x, leftLine.basePoint.x)),
      ({ y }) => y,
    );
    const rightPoint = extremaBy(
      cutFacetsHull.filter((point) => distanceEquals(point.x, rightLine.basePoint.x)),
      ({ y }) => y,
    );
    if (leftPoint == null || rightPoint == null)
      return wrapErr(CalculationErrorFactory.boundaryCornerFailure(top, leftPoint == null, rightPoint == null, true));
    const [startPoint, endPoint] = top ? [leftPoint, rightPoint] : [rightPoint, leftPoint];

    // Find and return subsequence of hull that represents boundary of truss solid
    const startPointIndex = cutFacetsHull.indexOf(startPoint);
    const hullOrderedFromStart = [
      ...takeRight(cutFacetsHull, cutFacetsHull.length - startPointIndex),
      ...take(cutFacetsHull, startPointIndex),
    ];
    const endPointIndex = hullOrderedFromStart.indexOf(endPoint);
    const boundary = take(hullOrderedFromStart, endPointIndex + 1).toReversed();
    return wrapOk({ cutFacets: shapes, boundary });
  }
}

const VERTICAL_PLANE_HINT_EXPANSION = 12;
